import { useCallback, useMemo, useState } from 'react'
import type { components } from '../../api/generated/schema'
import type { Notice } from '../../lib/envelope'
import { describeApiFailure } from '../../lib/failure'
import { encounterApi, useEncounterMutation } from './mutations'

type Schema = components['schemas']

/**
 * The body of `PUT /encounters/{id}`.
 *
 * Hand-widened rather than taken straight from the generated schema, because `type` is newer than
 * the last `npm run api:types` — the generator runs against a live API, so until it is next run
 * this is what says the field exists. It is the only field here that is not in the generated
 * shape; everything else is spread from it.
 *
 * `type` is genuinely optional and omitting it leaves the encounter as it is, so a rename does not
 * have to know what kind of encounter it is renaming.
 */
export type UpdateEncounterInput = Schema['UpdateEncounterRequest'] & {
  type?: Schema['EncounterType']
}

/**
 * Every encounter mutation, in one place.
 *
 * There is no `canEdit` flag here, and that is the access model rather than an omission:
 * encounters are Game Master-only for reads as well as writes, so anyone holding a sheet
 * may write it. The gate is on the entry point — the campaign page shows no encounters
 * link to a player at all.
 */
export function useEncounterEditing(encounterId: number) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeApiFailure(error, 'Encounters are the Game Master’s.'))
  }, [])

  const base = useMemo(
    () => ({ encounterId, onEnvelope, onFailure }),
    [encounterId, onEnvelope, onFailure],
  )

  const update = useEncounterMutation<UpdateEncounterInput>({
    ...base,
    request: (body) => encounterApi.update(encounterId, body),
  })

  const addSide = useEncounterMutation<{ sideName: string }>({
    ...base,
    request: ({ sideName }) => encounterApi.addSide(encounterId, sideName),
  })

  const renameSide = useEncounterMutation<{ sideId: number; sideName: string }>({
    ...base,
    request: ({ sideId, sideName }) => encounterApi.renameSide(encounterId, sideId, sideName),
  })

  const removeSide = useEncounterMutation<{ sideId: number }>({
    ...base,
    request: ({ sideId }) => encounterApi.removeSide(encounterId, sideId),
  })

  const addCharacter = useEncounterMutation<{ sideId: number; characterId: number }>({
    ...base,
    request: ({ sideId, characterId }) =>
      encounterApi.addCharacter(encounterId, sideId, characterId),
  })

  const removeCharacter = useEncounterMutation<{ sideId: number; characterId: number }>({
    ...base,
    request: ({ sideId, characterId }) =>
      encounterApi.removeCharacter(encounterId, sideId, characterId),
  })

  const all = [update, addSide, renameSide, removeSide, addCharacter, removeCharacter]

  return {
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    isPending: all.some((mutation) => mutation.isPending),
    update,
    addSide,
    renameSide,
    removeSide,
    addCharacter,
    removeCharacter,
  }
}

export type EncounterEditing = ReturnType<typeof useEncounterEditing>
